import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Autocomplete,
  Button,
  Card,
  CircularProgress,
  Drawer,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Skeleton,
  TextField,
  Tooltip,
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import {
  ArrowLeft,
  CalendarClock,
  Clock3,
  ChevronLeft,
  ChevronRight,
  Funnel,
  Info,
  MapPin,
  MapPinned,
  Minus,
  Plus,
  RefreshCw,
  ScanLine,
  UsersRound,
  X,
} from "lucide-react";
import { DateTime } from "luxon";
import {
  APIProvider,
  ControlPosition,
  Map,
  MapControl,
  useMap,
} from "@vis.gl/react-google-maps";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { getAttendanceHeatmap } from "../services/attendanceHeatmapApi";
import { getProjectDesignationOptions, getProjectSupervisorOptions } from "../services/attendanceApi";
import {
  attendanceHeatmapCssVariables,
  attendanceHeatmapTheme,
} from "../theme/attendanceHeatmapTheme";
import { normalizeDateTime } from "../utils/attendanceHeatmap";
import "./AttendanceHeatmap.css";

/** @typedef {"project" | "viewer"} TimezoneMode */

export { normalizeDateTime } from "../utils/attendanceHeatmap";

export function convertLocalDateTime(value, fromTimezone, toTimezone) {
  if (!value) return value;

  return DateTime
    .fromISO(value, { zone: fromTimezone })
    .setZone(toTimezone)
    .toFormat("yyyy-MM-dd'T'HH:mm");
}

function createRollingLast24Hours(timezone) {
  const to = DateTime.now()
    .setZone(timezone)
    .set({ second: 0, millisecond: 0 });
  const from = to.minus({ hours: 24 });

  return {
    from: from.toFormat("yyyy-MM-dd'T'HH:mm"),
    to: to.toFormat("yyyy-MM-dd'T'HH:mm"),
  };
}

function formatUtcForDisplay(value, timezone) {
  if (!value) return "";

  return DateTime
    .fromISO(value, { zone: "utc" })
    .setZone(timezone)
    .toFormat("MMM d, h:mm a");
}

const readSelectedProject = () => {
  try {
    const stored = JSON.parse(localStorage.getItem("selectedProject") || "null");
    return stored && typeof stored === "object" ? stored : {};
  } catch {
    return {};
  }
};

const heatmapErrorMessage = (error) => {
  const responseData = error?.response?.data;
  const backendMessage = responseData?.message || responseData?.error || responseData;
  const candidateMessage = typeof backendMessage === "string" ? backendMessage : "";
  const exposesStack = /traceback|stack trace|\n\s*at\s+/i.test(candidateMessage);
  const safeMessage = exposesStack ? "" : candidateMessage;
  const normalizedMessage = safeMessage.toLowerCase();

  if (normalizedMessage.includes("does not exist")) {
    return "This time is not available on the selected date. Please choose another time.";
  }
  if (normalizedMessage.includes("occurs twice")) {
    return "This time is not available on the selected date. Please choose another time.";
  }
  return safeMessage || "Unable to load attendance heatmap.";
};

function GoogleMapViewport({ points, fallbackCenter }) {
  const map = useMap();

  useEffect(() => {
    if (!map || !window.google) return;

    if (points.length > 1) {
      const bounds = new window.google.maps.LatLngBounds();
      points.forEach((point) => bounds.extend({ lat: point.lat, lng: point.lng }));
      map.fitBounds(
        bounds,
        56,
      );
      return;
    }

    const center = points.length === 1
      ? { lat: points[0].lat, lng: points[0].lng }
      : fallbackCenter;
    map.setCenter(center);
    map.setZoom(points.length === 1 ? 15 : 11);
  }, [fallbackCenter, map, points]);

  return null;
}

function GoogleMapZoomControls({ points, fallbackCenter }) {
  const map = useMap();
  const clampZoom = (delta) => {
    if (!map) return;
    const zoom = map.getZoom() || 11;
    map.setZoom(Math.max(3, Math.min(21, zoom + delta)));
  };
  const resetView = () => {
    if (!map || !window.google) return;
    if (points.length > 1) {
      const bounds = new window.google.maps.LatLngBounds();
      points.forEach((point) => bounds.extend({ lat: point.lat, lng: point.lng }));
      map.fitBounds(bounds, 56);
      return;
    }
    const center = points.length === 1
      ? { lat: points[0].lat, lng: points[0].lng }
      : fallbackCenter;
    map.setCenter(center);
    map.setZoom(points.length === 1 ? 15 : 11);
  };

  return (
    <MapControl position={ControlPosition.RIGHT_TOP} className="attendance-map-zoom-control">
      <button type="button" aria-label="Zoom in" title="Zoom in" onClick={() => clampZoom(1)}><Plus size={18} /></button>
      <button type="button" aria-label="Zoom out" title="Zoom out" onClick={() => clampZoom(-1)}><Minus size={18} /></button>
      <button type="button" className="attendance-map-reset-view" aria-label="Fit all locations on map" title="Fit all locations" onClick={resetView}>⌖</button>
    </MapControl>
  );
}

const heatColorStops = [
  [0, [255, 241, 118]],
  [0.28, [255, 213, 79]],
  [0.5, [255, 179, 0]],
  [0.72, [251, 140, 0]],
  [0.9, [239, 83, 80]],
  [1, [183, 28, 28]],
];

function heatColor(level) {
  const boundedLevel = Math.max(0, Math.min(1, level));
  const upperIndex = heatColorStops.findIndex(([stop]) => stop >= boundedLevel);
  if (upperIndex <= 0) return heatColorStops[0][1];
  const [upperStop, upperColor] = heatColorStops[upperIndex];
  const [lowerStop, lowerColor] = heatColorStops[upperIndex - 1];
  const mix = (boundedLevel - lowerStop) / (upperStop - lowerStop);
  return lowerColor.map((channel, index) => Math.round(channel + ((upperColor[index] - channel) * mix)));
}

function escapeMapPopupText(value) {
  return String(value || "").replace(/[&<>'"]/g, (character) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#39;", '"': "&quot;",
  }[character]));
}

function personScanSummary(points, field, idField, unavailableLabel) {
  const personCounts = new window.Map();
  points.forEach((point) => {
    (point[field] || []).forEach((person) => {
      const key = person[idField] || person.name;
      const existing = personCounts.get(key) || { name: person.name || unavailableLabel, scans: 0 };
      existing.scans += Number(person.scan_count) || 0;
      personCounts.set(key, existing);
    });
  });
  const people = [...personCounts.values()].sort((a, b) => b.scans - a.scans || a.name.localeCompare(b.name));
  if (!people.length) return `<span>${escapeMapPopupText(unavailableLabel)} information is unavailable for these scans.</span>`;
  return `<ul class="attendance-supervisor-list">${people.map((person) => `<li><strong>${escapeMapPopupText(person.name)}</strong><span>${person.scans} scan${person.scans === 1 ? "" : "s"}</span></li>`).join("")}</ul>`;
}

function GoogleAttendanceHeatmap({ points, visible }) {
  const map = useMap();

  useEffect(() => {
    // In inspection mode markers own the click interaction. Leaving this map
    // listener active would replace their detail popup with a heatmap summary.
    if (!map || !window.google || !visible) return undefined;

    const infoWindow = new window.google.maps.InfoWindow();
    const peakWeight = Math.max(1, ...points.map((point) => Number(point.unique_users) || 1));

    // Google retired HeatmapLayer. This OverlayView is a density raster: all
    // real point weights are accumulated in pixel space, then colourised.
    class DensityOverlay extends window.google.maps.OverlayView {
      constructor() {
        super();
        this.canvas = document.createElement("canvas");
        this.canvas.className = "attendance-density-overlay";
        this.canvas.style.cssText = "position:absolute;pointer-events:none;";
      }

      onAdd() { this.getPanes().overlayLayer.appendChild(this.canvas); }

      draw() {
        const projection = this.getProjection();
        const bounds = map.getBounds();
        if (!projection || !bounds) return;
        const northEast = projection.fromLatLngToDivPixel(bounds.getNorthEast());
        const southWest = projection.fromLatLngToDivPixel(bounds.getSouthWest());
        const width = Math.ceil(northEast.x - southWest.x);
        const height = Math.ceil(southWest.y - northEast.y);
        if (width <= 0 || height <= 0) return;

        this.canvas.width = width;
        this.canvas.height = height;
        this.canvas.style.left = `${southWest.x}px`;
        this.canvas.style.top = `${northEast.y}px`;
        const density = document.createElement("canvas");
        density.width = width;
        density.height = height;
        const densityContext = density.getContext("2d");
        const zoom = map.getZoom() || 11;
        // A broad, low-alpha outer radius keeps sparse activity visibly yellow
        // while dense overlap still resolves to a compact warm core.
        const radius = Math.max(22, Math.min(68, 68 - ((zoom - 11) * 3.5)));
        densityContext.globalCompositeOperation = "lighter";

        points.forEach((point) => {
          const pixel = projection.fromLatLngToDivPixel(new window.google.maps.LatLng(point.lat, point.lng));
          const weight = Math.max(1, Number(point.unique_users) || 0) / peakWeight;
          const gradient = densityContext.createRadialGradient(
            pixel.x - southWest.x, pixel.y - northEast.y, 0,
            pixel.x - southWest.x, pixel.y - northEast.y, radius,
          );
          gradient.addColorStop(0, `rgba(255,255,255,${0.4 * weight})`);
          gradient.addColorStop(0.2, `rgba(255,255,255,${0.34 * weight})`);
          gradient.addColorStop(0.52, `rgba(255,255,255,${0.18 * weight})`);
          gradient.addColorStop(0.78, `rgba(255,255,255,${0.07 * weight})`);
          gradient.addColorStop(1, "rgba(255,255,255,0)");
          densityContext.fillStyle = gradient;
          densityContext.fillRect(pixel.x - southWest.x - radius, pixel.y - northEast.y - radius, radius * 2, radius * 2);
        });

        const densityPixels = densityContext.getImageData(0, 0, width, height);
        const output = densityContext.createImageData(width, height);
        for (let index = 0; index < densityPixels.data.length; index += 4) {
          const intensity = densityPixels.data[index + 3] / 255;
          if (!intensity) continue;
          const level = Math.min(1, intensity / 0.7);
          const color = heatColor(level);
          output.data[index] = color[0];
          output.data[index + 1] = color[1];
          output.data[index + 2] = color[2];
          output.data[index + 3] = Math.round(Math.min(0.68, 0.1 + (intensity * 0.9)) * 255);
        }
        this.canvas.getContext("2d").putImageData(output, 0, 0);
      }

      onRemove() { this.canvas.remove(); }
    }

    const layer = new DensityOverlay();
    layer.setMap(map);
    const clickListener = map.addListener("click", (event) => {
      if (!event.latLng) return;
      const clicked = event.latLng.toJSON();
      const threshold = 0.028 / (2 ** Math.max(0, (map.getZoom() || 11) - 11));
      const nearby = points.filter((point) => {
        const latitudeDistance = point.lat - clicked.lat;
        const longitudeDistance = (point.lng - clicked.lng) * Math.cos(clicked.lat * Math.PI / 180);
        return Math.hypot(latitudeDistance, longitudeDistance) <= threshold;
      });
      if (!nearby.length) return;
      const participants = nearby.reduce((total, point) => total + (Number(point.unique_users) || 0), 0);
      const scans = nearby.reduce((total, point) => total + (Number(point.total_scans) || 0), 0);
      infoWindow.setContent(`<strong>${participants} participants</strong><br/>${nearby.length} nearby location${nearby.length === 1 ? "" : "s"}<br/>Total scans: ${scans}`);
      infoWindow.setPosition(event.latLng);
      infoWindow.open({ map });
    });

    return () => {
      infoWindow.close();
      layer.setMap(null);
      clickListener.remove();
    };
  }, [map, points, visible]);

  return null;
}

function GoogleAttendanceLocations({ points, heatmapVisible }) {
  const map = useMap();
  const [showLocations, setShowLocations] = useState(!heatmapVisible);

  useEffect(() => {
    if (!map) return undefined;
    const updateVisibility = () => setShowLocations(
      !heatmapVisible || (map.getZoom() || 0) >= 18,
    );
    updateVisibility();
    const zoomListener = map.addListener("zoom_changed", updateVisibility);
    return () => zoomListener.remove();
  }, [heatmapVisible, map]);

  useEffect(() => {
    if (!map || !window.google || !showLocations) return undefined;
    const infoWindow = new window.google.maps.InfoWindow();
    let selectedMarker = null;
    const locationIcon = (participantCount, selected = false) => {
      const label = Math.max(0, Number(participantCount) || 0);
      const baseSize = label >= 100 ? 26 : label >= 10 ? 23 : 19;
      const size = selected ? baseSize + 4 : baseSize;
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
        <defs><filter id="location-shadow" x="-50%" y="-50%" width="200%" height="200%"><feDropShadow dx="0" dy="1" stdDeviation="1" flood-color="#101828" flood-opacity=".3"/></filter></defs>
        <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 2}" fill="#D92D20" stroke="#ffffff" stroke-width="${selected ? 3 : 2}" filter="url(#location-shadow)"/>
        <text x="50%" y="53%" text-anchor="middle" dominant-baseline="middle" fill="#ffffff" font-family="Arial,sans-serif" font-size="${label >= 100 ? 9 : label >= 10 ? 10 : 10}" font-weight="700">${label}</text>
      </svg>`;
      return {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
        scaledSize: new window.google.maps.Size(size, size),
        anchor: new window.google.maps.Point(size / 2, size / 2),
      };
    };
    const markers = points.map((point) => {
      const marker = new window.google.maps.Marker({
        position: { lat: point.lat, lng: point.lng },
        icon: locationIcon(point.unique_users),
        title: `${point.unique_users} participants, ${point.total_scans} scans`,
      });
      marker.attendancePoint = point;
      marker.addListener("click", () => {
        if (selectedMarker && selectedMarker !== marker) {
          selectedMarker.setIcon(locationIcon(selectedMarker.attendancePoint?.unique_users));
          selectedMarker.setZIndex(undefined);
        }
        marker.setIcon(locationIcon(point.unique_users, true));
        marker.setZIndex(Number(window.google.maps.Marker.MAX_ZINDEX) + 1);
        selectedMarker = marker;
        infoWindow.setContent(`
          <div class="attendance-location-popup">
            <strong>Location activity</strong>
            <span>${point.unique_users} participant${point.unique_users === 1 ? "" : "s"} · ${point.total_scans} scan${point.total_scans === 1 ? "" : "s"}</span>
            <span class="attendance-popup-label">Scanned by</span>
            ${personScanSummary([point], "scan_supervisors", "user_id", "Supervisor")}
            <span class="attendance-popup-label">Participant scanned</span>
            ${personScanSummary([point], "scanned_participants", "participant_id", "Participant")}
          </div>
        `);
        infoWindow.open({ map, anchor: marker });
      });
      return marker;
    });
    const clusterer = new MarkerClusterer({
      map,
      markers,
      onClusterClick: (event, cluster) => {
        const clusterPoints = cluster.markers.map((marker) => marker.attendancePoint).filter(Boolean);
        const scans = clusterPoints.reduce((total, point) => total + (Number(point.total_scans) || 0), 0);
        infoWindow.setContent(`
          <div class="attendance-location-popup">
            <strong>Cluster activity</strong>
            <span>${scans} scan${scans === 1 ? "" : "s"} across ${clusterPoints.length} nearby location${clusterPoints.length === 1 ? "" : "s"}</span>
            <span class="attendance-popup-label">Scanned by</span>
            ${personScanSummary(clusterPoints, "scan_supervisors", "user_id", "Supervisor")}
            <span class="attendance-popup-label">Participants scanned</span>
            ${personScanSummary(clusterPoints, "scanned_participants", "participant_id", "Participant")}
          </div>
        `);
        infoWindow.setPosition(event.latLng || cluster.position);
        infoWindow.open({ map });
      },
      renderer: {
        render: (cluster) => {
          const locationCount = cluster.markers.length;
          const totalScans = cluster.markers.reduce(
            (total, marker) => total + (Number(marker.attendancePoint?.total_scans) || 0),
            0,
          );
          const label = String(totalScans);
          const size = totalScans >= 100 ? 38 : totalScans >= 10 ? 34 : 30;
          const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
            <defs><filter id="cluster-shadow" x="-40%" y="-40%" width="180%" height="180%"><feDropShadow dx="0" dy="1" stdDeviation="1.2" flood-color="#101828" flood-opacity=".28"/></filter></defs>
            <rect x="2" y="2" width="${size - 4}" height="${size - 4}" rx="${Math.max(8, size / 3)}" fill="#175CD3" stroke="#ffffff" stroke-width="2.25" filter="url(#cluster-shadow)"/>
            <text x="50%" y="53%" text-anchor="middle" dominant-baseline="middle" fill="#ffffff" font-family="Arial,sans-serif" font-size="${label.length >= 4 ? 8 : label.length >= 3 ? 9 : 11}" font-weight="700">${label}</text>
          </svg>`;
          return new window.google.maps.Marker({
            position: cluster.position,
            icon: {
              url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
              scaledSize: new window.google.maps.Size(size, size),
              anchor: new window.google.maps.Point(size / 2, size / 2),
            },
            title: `${totalScans} scan${totalScans === 1 ? "" : "s"} across ${locationCount} nearby location${locationCount === 1 ? "" : "s"}`,
            zIndex: Number(window.google.maps.Marker.MAX_ZINDEX) + totalScans,
          });
        },
      },
    });
    return () => {
      infoWindow.close();
      clusterer.clearMarkers();
      markers.forEach((marker) => window.google.maps.event.clearInstanceListeners(marker));
    };
  }, [map, points, showLocations]);
  return null;
}

function AttendanceGoogleMap({ points, fallbackCenter, heatmapVisible }) {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  if (!apiKey) {
    return <Alert severity="warning">Google Maps API key is not configured.</Alert>;
  }

  return (
    <APIProvider apiKey={apiKey}>
      <Map
      className="attendance-leaflet-map"
      defaultCenter={fallbackCenter}
      defaultZoom={11}
      mapTypeId="satellite"
      gestureHandling="greedy"
      disableDefaultUI={false}
      zoomControl={false}
      mapTypeControlOptions={{ position: ControlPosition.TOP_CENTER }}
      fullscreenControlOptions={{ position: ControlPosition.RIGHT_TOP }}
      streetViewControlOptions={{ position: ControlPosition.RIGHT_TOP }}
      >
        <GoogleMapViewport points={points} fallbackCenter={fallbackCenter} />
        <GoogleMapZoomControls points={points} fallbackCenter={fallbackCenter} />
        <GoogleAttendanceHeatmap points={points} visible={heatmapVisible} />
        <GoogleAttendanceLocations points={points} heatmapVisible={heatmapVisible} />
      </Map>
    </APIProvider>
  );
}

function MapDisplayControls({ heatmapVisible, onToggle }) {
  return (
    <Card component="aside" elevation={0} className="attendance-map-display-controls"
      aria-label="Map display controls">
      <div className="attendance-map-display-toggle">
        <span>Heatmap</span>
        <Button
          className={heatmapVisible ? "active" : ""}
          size="small"
          aria-label={`Turn heatmap ${heatmapVisible ? "off" : "on"}`}
          aria-pressed={heatmapVisible}
          onClick={onToggle}
        >
          {heatmapVisible ? "On" : "Off"}
        </Button>
      </div>
      <div className="attendance-map-display-detail">
        {heatmapVisible ? <>
          <strong>Density</strong>
          <div className="attendance-legend-gradient" />
          <div className="attendance-legend-labels"><span>Low</span><span>High</span></div>
        </> : <div className="attendance-marker-key" aria-label="Map marker key">
          <div><span className="attendance-marker-key-symbol attendance-marker-key-location">12</span><span><strong>Total scans nearby</strong><small>Across grouped locations</small></span></div>
          <div><span className="attendance-marker-key-symbol attendance-marker-key-participant">1</span><span><strong>Participants</strong><small>At one exact location</small></span></div>
        </div>}
      </div>
    </Card>
  );
}

export default function AttendanceHeatmap({ isSidebarExpanded }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId } = useParams();
  const selectedProject = useMemo(readSelectedProject, []);
  const project = location.state?.project || selectedProject;
  const viewerTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const configuredProjectTimezone = project?.client?.timezone || project?.timezone || viewerTimezone;
  const initialRange = useMemo(
    () => createRollingLast24Hours(configuredProjectTimezone),
    [configuredProjectTimezone],
  );
  const [timezoneMode, setTimezoneMode] = useState(/** @type {TimezoneMode} */ ("project"));
  const [fromValue, setFromValue] = useState(initialRange.from);
  const [toValue, setToValue] = useState(initialRange.to);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [heatmapResponse, setHeatmapResponse] = useState(null);
  const [heatmapVisible, setHeatmapVisible] = useState(true);
  const [summaryCollapsed, setSummaryCollapsed] = useState(false);
  const [summaryTab, setSummaryTab] = useState("attendance");
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [applyAttempted, setApplyAttempted] = useState(false);
  const [filterInteractionStarted, setFilterInteractionStarted] = useState(false);
  const [supervisorOptions, setSupervisorOptions] = useState([]);
  const [designationOptions, setDesignationOptions] = useState([]);
  const [draftSupervisorId, setDraftSupervisorId] = useState("");
  const [draftDesignation, setDraftDesignation] = useState("");
  const [appliedSupervisorId, setAppliedSupervisorId] = useState("");
  const [appliedDesignation, setAppliedDesignation] = useState("");
  const requestController = useRef(null);
  // The API resolves the client/project timezone on the server. Prefer it to
  // the lightweight project object persisted in local storage.
  const projectTimezone = heatmapResponse?.project_timezone || configuredProjectTimezone;
  const activeTimezone = timezoneMode === "project" ? projectTimezone : viewerTimezone;
  const timeLabel = timezoneMode === "project" ? "Project time" : "My time";
  const fromValidationError = fromValue ? "" : "From is required.";
  const rangeExceedsMaximum = Boolean(fromValue && toValue) && (
    DateTime.fromISO(toValue, { zone: activeTimezone })
      .diff(DateTime.fromISO(fromValue, { zone: activeTimezone }), "days").days > 31
  );
  const toValidationError = !toValue
    ? "To is required."
    : fromValue && normalizeDateTime(toValue) <= normalizeDateTime(fromValue)
      ? "To must be later than From."
      : rangeExceedsMaximum
        ? "Date range cannot exceed 31 days."
      : "";
  const filtersInvalid = Boolean(fromValidationError || toValidationError);
  const showValidation = applyAttempted || filterInteractionStarted;
  const fromError = showValidation ? fromValidationError : "";
  const toError = showValidation ? toValidationError : "";
  const heatmapPoints = heatmapResponse?.data || [];
  const presenceSummary = heatmapResponse?.presence_summary;
  const designationSummary = heatmapResponse?.designation_summary || [];
  const scannedParticipants = presenceSummary?.seen_at_least_once ?? 0;
  const attentionCount = presenceSummary?.attention_count ?? 0;
  const rolePeakCount = Math.max(1, ...designationSummary.map(({ count }) => count));
  const hasNoActivity = Boolean(heatmapResponse) && heatmapPoints.length === 0;
  const fallbackCenter = useMemo(() => ({
    lat: Number(
      project?.latitude
      ?? project?.lat
      ?? project?.coordinates?.lat
      ?? project?.client?.latitude
      ?? 20.5937,
    ),
    lng: Number(
      project?.longitude
      ?? project?.lng
      ?? project?.coordinates?.lng
      ?? project?.client?.longitude
      ?? 78.9629,
    ),
  }), [project]);
  const totalScans = heatmapPoints.reduce((total, point) => total + point.total_scans, 0);
  const peakUniqueUsers = Math.max(
    0,
    ...heatmapPoints.map((point) => point.unique_users),
  );
  const appliedRange = heatmapResponse?.requested_range;
  const appliedViewerTimezone = heatmapResponse?.viewer_timezone || viewerTimezone;
  const selectedTimeRange = appliedRange
    ? `${formatUtcForDisplay(appliedRange.start_utc, appliedViewerTimezone)} – ${formatUtcForDisplay(appliedRange.end_utc, appliedViewerTimezone)}`
    : null;
  const insightCards = [
    {
      label: "Total Scans",
      value: heatmapResponse ? totalScans : null,
      icon: ScanLine,
    },
    {
      label: "Peak Unique Users at a Location",
      value: heatmapResponse ? peakUniqueUsers : null,
      icon: UsersRound,
    },
    {
      label: "Active Locations",
      value: heatmapResponse?.total_locations,
      icon: MapPin,
    },
  ];
  const appliedFilterCount = (heatmapResponse?.requested_range ? 1 : 0)
    + Number(Boolean(appliedSupervisorId))
    + Number(Boolean(appliedDesignation));

  const loadHeatmap = useCallback(async ({
    startDate,
    endDate,
    mode,
    supervisorId = "",
    designation = "",
    closeDrawer = false,
  }) => {
    requestController.current?.abort();
    const controller = new AbortController();
    requestController.current = controller;
    setError("");
    setLoading(true);

    if (closeDrawer) {
      setFilterDrawerOpen(false);
    }

    try {
      const response = await getAttendanceHeatmap(projectId, {
        startDate,
        endDate,
        timezoneMode: mode,
        viewerTimezone,
        supervisorId,
        designation,
      }, controller.signal);
      setHeatmapResponse(response);
      // Use the server-confirmed, timezone-resolved range everywhere in the UI.
      // This keeps the header's applied period and the drawer's editable values aligned.
      const confirmedRange = response?.requested_range;
      if (confirmedRange?.start_input && confirmedRange?.end_input) {
        setFromValue(DateTime.fromISO(confirmedRange.start_input).toFormat("yyyy-MM-dd'T'HH:mm"));
        setToValue(DateTime.fromISO(confirmedRange.end_input).toFormat("yyyy-MM-dd'T'HH:mm"));
      }
    } catch (requestError) {
      if (requestError?.code !== "ERR_CANCELED") {
        setError(heatmapErrorMessage(requestError));
      }
    } finally {
      if (requestController.current === controller) {
        requestController.current = null;
        setLoading(false);
      }
    }
  }, [projectId, viewerTimezone]);

  useEffect(() => {
    if (!projectId) {
      setSupervisorOptions([]);
      setDesignationOptions([]);
      return undefined;
    }
    const controller = new AbortController();
    getProjectSupervisorOptions(projectId, controller.signal)
      .then((users) => {
        const uniqueUsers = new window.Map();
        (Array.isArray(users) ? users : []).forEach((user) => {
          const name = String(user?.user_name || "").trim();
          if (!name) return;
          const key = name.toLocaleLowerCase();
          if (!uniqueUsers.has(key)) uniqueUsers.set(key, { ...user, user_name: name });
        });
        setSupervisorOptions([...uniqueUsers.values()].sort((first, second) => (
          first.user_name.localeCompare(second.user_name)
        )));
      })
      .catch((requestError) => {
        if (requestError?.code !== "ERR_CANCELED") setSupervisorOptions([]);
      });
    getProjectDesignationOptions(projectId, controller.signal)
      .then((designations) => setDesignationOptions(Array.isArray(designations) ? designations : []))
      .catch((requestError) => {
        if (requestError?.code !== "ERR_CANCELED") setDesignationOptions([]);
      });
    return () => controller.abort();
  }, [projectId]);

  useEffect(() => {
    loadHeatmap({
      startDate: initialRange.from,
      endDate: initialRange.to,
      mode: "project",
      supervisorId: "",
      designation: "",
    });
  }, [initialRange.from, initialRange.to, loadHeatmap]);

  useEffect(() => () => {
    const controller = requestController.current;
    requestController.current = null;
    controller?.abort();
  }, []);

  const resetFilters = () => {
    const defaultRange = createRollingLast24Hours(activeTimezone);
    setFromValue(defaultRange.from);
    setToValue(defaultRange.to);
    setApplyAttempted(false);
    setFilterInteractionStarted(false);
    setDraftSupervisorId("");
    setDraftDesignation("");
    setAppliedSupervisorId("");
    setAppliedDesignation("");
    loadHeatmap({
      startDate: defaultRange.from,
      endDate: defaultRange.to,
      mode: timezoneMode,
      supervisorId: "",
      designation: "",
      closeDrawer: true,
    });
  };

  const changeTimezoneMode = (event) => {
    const nextMode = event.target.value;
    if (nextMode === timezoneMode) return;

    const previousTimezone = timezoneMode === "project" ? projectTimezone : viewerTimezone;
    const nextTimezone = nextMode === "project" ? projectTimezone : viewerTimezone;

    setFromValue((value) => convertLocalDateTime(value, previousTimezone, nextTimezone));
    setToValue((value) => convertLocalDateTime(value, previousTimezone, nextTimezone));
    setTimezoneMode(nextMode);
  };

  const applyFilters = async () => {
    if (loading) return;
    setApplyAttempted(true);
    setError("");
    if (filtersInvalid) return;
    loadHeatmap({
      startDate: fromValue,
      endDate: toValue,
      mode: timezoneMode,
      supervisorId: draftSupervisorId,
      designation: draftDesignation,
      closeDrawer: true,
    });
    setAppliedSupervisorId(draftSupervisorId);
    setAppliedDesignation(draftDesignation);
  };

  const pickerValue = (value) => (
    value ? DateTime.fromISO(value, { zone: activeTimezone }) : null
  );
  const updatePickerDate = (setter, currentValue) => (value) => {
    setFilterInteractionStarted(true);
    if (!value?.isValid) {
      setter("");
      return;
    }
    const time = currentValue?.slice(11, 16) || "00:00";
    setter(`${value.toFormat("yyyy-MM-dd")}T${time}`);
  };
  const updatePickerTime = (setter, currentValue) => (event) => {
    setFilterInteractionStarted(true);
    if (!currentValue) return;
    setter(`${currentValue.slice(0, 10)}T${event.target.value}`);
  };

  return (
    <ThemeProvider theme={attendanceHeatmapTheme}>
      <LocalizationProvider dateAdapter={AdapterLuxon}>
        <div
          className={`table-container attendance-page attendance-heatmap-page ${isSidebarExpanded ? "expanded" : "collapsed"}`}
          style={attendanceHeatmapCssVariables}
        >
          <main className="attendance-heatmap-dashboard">
            <header className="attendance-heatmap-page-header">
              <div className="attendance-heatmap-heading-group">
                <Tooltip title="Back to Attendance">
                  <IconButton className="attendance-heatmap-back" aria-label="Back to Attendance"
                    onClick={() => navigate("/attendance")}>
                    <ArrowLeft size={19} aria-hidden="true" />
                  </IconButton>
                </Tooltip>
                <div className="attendance-heatmap-heading">
                  <h2>Attendance Heatmap</h2>
                  <p>
                    {""}
                    <Tooltip title={projectId || ""}>
                      <span className="attendance-project-id">{null}</span>
                    </Tooltip>
                    <span aria-hidden="true"> · </span>
                    {projectTimezone}
                  </p>
                </div>
              </div>

              <div className="attendance-heatmap-header-actions">
                <Button className="attendance-selected-range" variant="outlined"
                  startIcon={<CalendarClock size={16} />} onClick={() => setFilterDrawerOpen(true)}>
                  <span>{selectedTimeRange || "Select date range"}</span>
                </Button>
                <Button className="attendance-filter-button" variant="outlined"
                  startIcon={<Funnel size={16} />} onClick={() => setFilterDrawerOpen(true)}>
                  Filters
                  {appliedFilterCount > 0 && <span className="attendance-filter-count">{appliedFilterCount}</span>}
                </Button>
                <Tooltip title="Refresh map data"><IconButton className="attendance-heatmap-refresh" aria-label="Refresh map data" disabled={loading} onClick={() => loadHeatmap({ startDate: fromValue, endDate: toValue, mode: timezoneMode, supervisorId: appliedSupervisorId, designation: appliedDesignation })}><RefreshCw size={17} className={loading ? "is-loading" : ""} /></IconButton></Tooltip>
              </div>
            </header>

            <section className="attendance-map-stage" aria-label="Attendance heatmap">
              <AttendanceGoogleMap
                points={heatmapPoints}
                fallbackCenter={fallbackCenter}
                heatmapVisible={heatmapVisible}
              />

              <MapDisplayControls heatmapVisible={heatmapVisible}
                onToggle={() => setHeatmapVisible((visible) => !visible)} />

              {error && <Alert severity="error" className="attendance-heatmap-floating-error">{error}</Alert>}

              {(!heatmapResponse || hasNoActivity) && !loading && (
                <Card className="attendance-map-empty" role="status" elevation={0}>
                  <MapPinned size={28} aria-hidden="true" />
                  <strong>{hasNoActivity ? "No scan activity found" : "No heatmap data selected"}</strong>
                  <p>{hasNoActivity
                    ? "Try selecting a wider date or time range."
                    : "Choose a date and time range to display attendance activity."}</p>
                  <Button variant="contained" onClick={() => setFilterDrawerOpen(true)}>
                    Select date range
                  </Button>
                </Card>
              )}

              {loading && <div className="attendance-map-loading" role="status"
                aria-label="Loading attendance heatmap">
                <CircularProgress size={28} />
              </div>}

              {summaryCollapsed ? (
                <Card className="attendance-summary-collapsed" elevation={0} aria-label="Collapsed attendance summary">
                  <div className="attendance-summary-collapsed-kpis">
                    <span><strong>{loading ? "—" : scannedParticipants}</strong>scanned</span>
                    <span><strong>{loading ? "—" : designationSummary.length}</strong>roles</span>
                  </div>
                  <Tooltip title="Show attendance summary" placement="right">
                    <IconButton className="attendance-summary-expand" aria-label="Show attendance summary"
                      onClick={() => setSummaryCollapsed(false)}>
                      <ChevronRight size={21} />
                    </IconButton>
                  </Tooltip>
                </Card>
              ) : (
                <div className={`attendance-summary-shell ${summaryTab === "roles" ? "roles-active" : "attendance-active"}`}>
                <Card component="section" elevation={0} className={`attendance-presence-panel ${summaryTab === "roles" ? "roles-tab" : ""}`} aria-label="Attendance summary">
                  <div className="attendance-summary-tabs" role="tablist" aria-label="Attendance summary views">
                    <Button role="tab" aria-selected={summaryTab === "attendance"}
                      className={summaryTab === "attendance" ? "active" : ""}
                      onClick={() => setSummaryTab("attendance")}>Attendance</Button>
                    <Button role="tab" aria-selected={summaryTab === "roles"}
                      className={summaryTab === "roles" ? "active" : ""}
                      onClick={() => setSummaryTab("roles")}>Roles scanned</Button>
                  </div>

                  <div className="attendance-kpis">
                    <div><span>Scanned</span><strong>{loading ? "—" : scannedParticipants}</strong></div>
                    <div><span>Need attention</span><strong className="danger">{loading ? "—" : attentionCount}</strong></div>
                    <div><span>Roles</span><strong>{loading ? "—" : designationSummary.length}</strong></div>
                  </div>

                  {summaryTab === "attendance" && <>
                    <div className="attendance-seen-highlight">
                      <div><strong>Seen at least once</strong><span>{loading ? "—" : scannedParticipants}</span></div>
                      <small>{presenceSummary?.total_participants
                        ? `${((scannedParticipants / presenceSummary.total_participants) * 100).toFixed(1)}% of ${presenceSummary.total_participants.toLocaleString()} participants`
                        : "0% of participants"}</small>
                      <Tooltip title="Presence is calculated from scans in the selected date range. NFC not configured is an overlapping status and is not added to the attention total."><Info size={16} /></Tooltip>
                    </div>
                    <Button className="attendance-presence-attention" onClick={() => setFilterDrawerOpen(true)}>
                      ⚠ {attentionCount.toLocaleString()} need attention in selected period <ChevronRight size={17} />
                    </Button>
                    <div className="attendance-presence-heading"><strong>Presence in selected period</strong><Info size={15} /></div>
                    {[['seen_last_15', 'Seen in last 15 min', 'fresh'], ['seen_15_30', 'Seen 15–30 min', 'recent'], ['seen_30_60', 'Seen 30–60 min', 'aging'], ['last_seen_over_60', 'Last seen > 60 min', 'stale'], ['never_scanned', 'No scans in selected period', 'never'], ['nfc_not_configured', 'NFC not configured (overlaps)', 'nfc']].map(([key, label, tone]) => <div className="attendance-presence-row" key={key}><span className={`attendance-presence-dot ${tone}`} /><span>{label}</span><strong>{loading ? '—' : presenceSummary?.[key] ?? 0}</strong><small>{presenceSummary?.total_participants ? `${((presenceSummary[key] / presenceSummary.total_participants) * 100).toFixed(1)}%` : '0%'}</small></div>)}
                  </>}

                  {summaryTab === "roles" && <div className="attendance-designation-summary is-active" aria-live="polite">
                    <div className="attendance-designation-heading">
                      <Tooltip title="Unique participants with at least one scan in the selected period."><strong>Scanned roles ({designationSummary.length})</strong></Tooltip>
                    </div>
                    {loading ? <span className="attendance-designation-empty">Loading…</span> : designationSummary.length ? (
                      <div className="attendance-designation-list">
                        {designationSummary.map(({ designation, count }) => (
                          <div className="attendance-designation-row" key={designation}>
                            <span title={designation}>{designation}</span>
                            <i><b style={{ width: `${(count / rolePeakCount) * 100}%` }} /></i>
                            <strong>{count}</strong>
                          </div>
                        ))}
                      </div>
                    ) : <span className="attendance-designation-empty">No participants scanned</span>}
                  </div>}
                </Card>
                <Tooltip title="Collapse attendance summary" placement="right">
                  <IconButton className="attendance-summary-collapse" aria-label="Collapse attendance summary"
                    onClick={() => setSummaryCollapsed(true)}>
                    <ChevronLeft size={17} />
                  </IconButton>
                </Tooltip>
                </div>
              )}

            </section>
          </main>
        </div>

        <Drawer
          anchor="right"
          open={filterDrawerOpen}
          onClose={() => setFilterDrawerOpen(false)}
          className="attendance-filter-drawer"
          slotProps={{
            paper: {
              className: "attendance-filter-drawer-paper",
              style: attendanceHeatmapCssVariables,
            },
          }}
        >
          <div className="attendance-filter-drawer-header">
            <div>
              <h2>Heatmap Filters</h2>
              <p>Select the period you want to analyse.</p>
            </div>
            <Tooltip title="Close filters">
              <IconButton aria-label="Close heatmap filters" onClick={() => setFilterDrawerOpen(false)}>
                <X size={19} aria-hidden="true" />
              </IconButton>
            </Tooltip>
          </div>

          <div className="attendance-filter-drawer-body">
            <div className="attendance-drawer-field">
              <div className="attendance-drawer-label">
                <label htmlFor="attendance-heatmap-timezone">View time in</label>
                <Tooltip title={timezoneMode === "project"
                  ? `Filters use project time (${projectTimezone})`
                  : `Project timezone is ${projectTimezone}; filters use your local time`}>
                  <IconButton className="attendance-filter-info" aria-label="Timezone filter information">
                    <Info size={15} aria-hidden="true" />
                  </IconButton>
                </Tooltip>
              </div>
              <Select id="attendance-heatmap-timezone" className="attendance-timezone-select" size="small"
                value={timezoneMode} onChange={changeTimezoneMode}
                startAdornment={<InputAdornment position="start"><Clock3 size={15} /></InputAdornment>}
                inputProps={{ "aria-label": "View time in" }}>
                <MenuItem value="project">Project time — {projectTimezone}</MenuItem>
                <MenuItem value="viewer">My time — {viewerTimezone}</MenuItem>
              </Select>
            </div>

            <div className="attendance-timezone-strip" role="note">
              <Info size={15} aria-hidden="true" />
              <span>{timezoneMode === "project"
                ? `All selected dates and times will be interpreted in project time (${projectTimezone}).`
                : `All selected dates and times will be interpreted in your time (${viewerTimezone}).`}</span>
            </div>

            <div className="attendance-drawer-field">
              <label htmlFor="attendance-heatmap-supervisor">Supervisor</label>
              <Autocomplete
                id="attendance-heatmap-supervisor"
                className="attendance-heatmap-autocomplete"
                options={supervisorOptions}
                value={supervisorOptions.find((user) => user?.user_id === draftSupervisorId) || null}
                size="small"
                autoHighlight
                clearOnEscape
                openOnFocus
                noOptionsText="No supervisors found"
                getOptionLabel={(user) => user?.user_name || ""}
                isOptionEqualToValue={(option, value) => option?.user_id === value?.user_id}
                onChange={(_, user) => setDraftSupervisorId(user?.user_id || "")}
                renderInput={(params) => <TextField {...params} placeholder="All supervisors" inputProps={{ ...params.inputProps, "aria-label": "Filter heatmap by supervisor" }} />}
              />
            </div>

            <div className="attendance-drawer-field">
              <label htmlFor="attendance-heatmap-designation">Designation</label>
              <Autocomplete
                id="attendance-heatmap-designation"
                className="attendance-heatmap-autocomplete"
                options={designationOptions}
                value={draftDesignation || null}
                size="small"
                autoHighlight
                clearOnEscape
                openOnFocus
                noOptionsText="No designations found"
                onChange={(_, value) => setDraftDesignation(value || "")}
                renderInput={(params) => <TextField {...params} placeholder="All designations" inputProps={{ ...params.inputProps, "aria-label": "Filter heatmap by designation" }} />}
              />
            </div>

            <div className="attendance-drawer-field attendance-date-time-field">
              <label>From</label>
              <div className="attendance-date-time-row">
              <DatePicker
                label="Date"
                value={pickerValue(fromValue)}
                onChange={updatePickerDate(setFromValue, fromValue)}
                timezone={activeTimezone}
                format="dd MMM yyyy"
                closeOnSelect={false}
                slotProps={{
                  textField: {
                    id: "attendance-heatmap-from",
                    size: "small",
                    error: Boolean(fromError),
                    inputProps: { "aria-label": `From date — ${timeLabel}` },
                  },
                  actionBar: { actions: ["cancel", "accept"] },
                  popper: {
                    placement: "bottom-end",
                    modifiers: [
                      { name: "flip", enabled: true },
                      { name: "preventOverflow", enabled: true, options: { boundary: "viewport", padding: 12 } },
                    ],
                  },
                }}
              />
              <TextField label="Time" type="time" size="small" disabled={!fromValue}
                value={fromValue?.slice(11, 16) || ""}
                onChange={updatePickerTime(setFromValue, fromValue)}
                inputProps={{ "aria-label": `From time — ${timeLabel}`, step: 300 }} />
              </div>
              {fromError && <span className="attendance-drawer-error">{fromError}</span>}
            </div>

            <div className="attendance-drawer-field attendance-date-time-field">
              <label>To</label>
              <div className="attendance-date-time-row">
              <DatePicker
                label="Date"
                value={pickerValue(toValue)}
                onChange={updatePickerDate(setToValue, toValue)}
                timezone={activeTimezone}
                format="dd MMM yyyy"
                minDate={pickerValue(fromValue) || undefined}
                closeOnSelect={false}
                slotProps={{
                  textField: {
                    id: "attendance-heatmap-to",
                    size: "small",
                    error: Boolean(toError),
                    inputProps: { "aria-label": `To date — ${timeLabel}` },
                  },
                  actionBar: { actions: ["cancel", "accept"] },
                  popper: {
                    placement: "bottom-end",
                    modifiers: [
                      { name: "flip", enabled: true },
                      { name: "preventOverflow", enabled: true, options: { boundary: "viewport", padding: 12 } },
                    ],
                  },
                }}
              />
              <TextField label="Time" type="time" size="small" disabled={!toValue}
                value={toValue?.slice(11, 16) || ""}
                onChange={updatePickerTime(setToValue, toValue)}
                inputProps={{ "aria-label": `To time — ${timeLabel}`, step: 300 }} />
              </div>
              {toError && <span className="attendance-drawer-error">{toError}</span>}
            </div>

            <div className="attendance-range-helper">
              <CalendarClock size={14} aria-hidden="true" />
              <span>Maximum range: 31 days</span>
            </div>
          </div>

          <div className="attendance-filter-drawer-actions">
            <Button variant="outlined" onClick={resetFilters} disabled={loading}>
              Reset to Default
            </Button>
            <Button variant="contained" onClick={applyFilters} disabled={filtersInvalid || loading}>
              {loading ? "Applying…" : "Apply Filters"}
            </Button>
          </div>
        </Drawer>
      </LocalizationProvider>
    </ThemeProvider>
  );
}
